import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Form, Formik } from 'formik'
import { useState } from 'react'
import { MdNotInterested } from 'react-icons/md'

import type { ReactElement } from 'react'
import type { IAppSummary } from '~/types/application'
import type { IPageContainer } from '~/types/page-container'
import type { IPageContainerFormValues } from '~/types/template'

import PlaceholderForm from '../Form/Editor/Form'
import UploadScreenshotsModal from '../Form/UploadScreenshots'

import { createFile, uploadFile } from '~/api/file'
import { updatePageContainer } from '~/api/page-containers'
import Button from '~/components/Buttons/Button'
import SelectInput from '~/components/Inputs/Basic/SelectInput'
import Modal from '~/components/Modal'
import PaginatedPreview from '~/components/PaginatedPreview'
import { usePageContainerDetails } from '~/hooks/usePageContainerDetails'
import { pageContainersKeys } from '~/queries/page-containers'
import { getInitialValues } from '~/utils/page-container'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface Props {
  pageContainer: IPageContainer
  selectedApp: IAppSummary
  templateKey?: string
}

const TemplateNotFound = ({ pageContainer, selectedApp }: Props): ReactElement => {
  const [isUploadScreenshotsModalOpen, setIsUploadScreenshotsModalOpen] = useState(false)

  const { mutateAsync: updatePageContainerMutation } = useMutation({ mutationFn: updatePageContainer })

  const { pageContainerDetails } = usePageContainerDetails({ pageContainer, selectedApp })

  const initialValues: IPageContainerFormValues = getInitialValues({
    isUnity: pageContainerDetails?.is_unity || false,
    pageContainerDetails,
    selectedApp,
  })

  const queryClient = useQueryClient()

  const placeholderOptions = [...new Set(initialValues.placeholders.map(({ key }) => key))].map((key) => ({
    label: key,
    value: key,
  }))

  const existingScreenshots = pageContainerDetails?.screenshot_file_urls?.reduce(
    (prev, url, index) => {
      if (pageContainerDetails?.screenshot_file_ids?.[index]) {
        prev[pageContainerDetails.screenshot_file_ids[index]] = url
      } else {
        prev[index.toString()] = url
      }
      return prev
    },
    {} as Record<string, string>
  )

  const handleSubmit = () => {
    console.log('submit')
  }

  const toggleUploadScreenshotsModal = (isOpen: boolean) => () => setIsUploadScreenshotsModalOpen(isOpen)

  const handleUploadScreenshots = async (files: File[], remainingScreenshots?: Record<string, string>) => {
    if (!pageContainerDetails?.uuid) return

    try {
      const fileIds = new Array(files.length)
      await Promise.all(
        files.map(async (file, index) => {
          const { id, upload_url } = await createFile({
            mimeType: file.type,
            name: file.name,
            size: file.size,
          })
          await uploadFile({ file, mimeType: file.type, uploadUrl: upload_url })
          fileIds[index] = id
        })
      )
      await updatePageContainerMutation({
        id: pageContainerDetails.uuid,
        screenshotFileIds: [...Object.keys(remainingScreenshots || {}), ...fileIds],
        title: pageContainerDetails?.title,
      })
      queryClient.invalidateQueries({ queryKey: pageContainersKeys.details(pageContainerDetails.uuid) })
      displayToast('Screenshots successfully uploaded', 'success')
      setIsUploadScreenshotsModalOpen(false)
    } catch {
      displayToast('Screenshots could not be uploaded', 'error')
    }
  }

  return (
    <div className="no-template-container template-not-found">
      <div className="template-not-found__actions">
        <Button onClick={toggleUploadScreenshotsModal(true)}>Upload screenshots</Button>
      </div>
      <div className="page-container-details">
        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          {({ setFieldValue, values }) => {
            return (
              <Form className="page-container-details__placeholders">
                {values.placeholders.length > 0 ? (
                  <>
                    <SelectInput
                      className="placeholders-search"
                      isClearable
                      onChange={(option) => {
                        if (option) {
                          setFieldValue('focus', (option as IOption).value)
                        } else {
                          setFieldValue('focus', undefined)
                        }
                      }}
                      options={placeholderOptions}
                      placeholder="Select placeholder"
                      size="small"
                      value={values.focus ? { label: values.focus, value: values.focus } : undefined}
                    />
                    <div className="placeholders-container">
                      <PlaceholderForm className="placeholders-container__form" placeholders={values.placeholders} />
                    </div>
                  </>
                ) : (
                  <div className="no-placeholders-message">
                    <div>No placeholders data available</div>
                    <MdNotInterested size={22} />
                  </div>
                )}
              </Form>
            )
          }}
        </Formik>
        <div className="page-container-details__screenshots">
          {pageContainerDetails?.screenshot_file_urls && pageContainerDetails?.screenshot_file_urls.length > 0 ? (
            <>
              <PaginatedPreview previewUrls={pageContainerDetails.screenshot_file_urls} />
            </>
          ) : (
            <div className="no-screenshot-message">
              No screenshot available
              <Button onClick={toggleUploadScreenshotsModal(true)}>Upload</Button>
            </div>
          )}
        </div>
      </div>
      <Modal
        className="upload-screenshots-modal"
        isOpen={isUploadScreenshotsModalOpen}
        onCloseModal={toggleUploadScreenshotsModal(false)}
      >
        <UploadScreenshotsModal
          existingScreenshots={existingScreenshots}
          onClose={toggleUploadScreenshotsModal(false)}
          onSubmit={handleUploadScreenshots}
        />
      </Modal>
    </div>
  )
}

export default TemplateNotFound
